import React, { useState } from 'react'
import { COMPANY } from '../data/siteData'

export default function ContactForm({
  defaultService = '',
  compact = false,
  variant = 'card',
  hideHeader = false
}) {
  const [formData, setFormData] = useState({
    navn: '',
    telefonnummer: '',
    email: '',
    service: defaultService || 'Belægning',
    beskrivelse: '',
    website_url: '' // Anti-spam honeypot
  })

  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('')

  function validate() {
    const newErrors = {}
    if (!formData.navn.trim()) {
      newErrors.navn = 'Indtast venligst dit fulde navn'
    }
    if (!formData.telefonnummer.trim()) {
      newErrors.telefonnummer = 'Indtast venligst et gyldigt telefonnummer'
    } else if (!/^[+0-9\s-]{8,20}$/.test(formData.telefonnummer.trim())) {
      newErrors.telefonnummer = 'Telefonnummeret ser ikke ud til at være gyldigt'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Indtast venligst din e-mailadresse'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast venligst en gyldig e-mailadresse'
    }
    if (!formData.beskrivelse.trim()) {
      newErrors.beskrivelse = 'Beskriv venligst kort din opgave'
    } else if (formData.beskrivelse.trim().length < 10) {
      newErrors.beskrivelse = 'Giv venligst en lidt mere fyldestgørende beskrivelse af opgaven (min. 10 tegn)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()

    // Honeypot check: If bot filled it, pretend success but drop
    if (formData.website_url) {
      setStatus('success')
      return
    }

    if (!validate()) {
      return
    }

    setStatus('submitting')
    setErrorMessage('')

    try {
      const payload = {
        name: formData.navn,
        phone: formData.telefonnummer,
        email: formData.email,
        service: formData.service,
        message: formData.beskrivelse,
        website_url: formData.website_url
      }

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          navn: '',
          telefonnummer: '',
          email: '',
          service: defaultService || 'Nedgravning af trampolin',
          beskrivelse: '',
          website_url: ''
        })
      } else {
        const errorData = await response.json().catch(() => null)
        setStatus('error')
        setErrorMessage(
          errorData?.message ||
            'Der opstod en fejl under afsendelsen. Prøv venligst igen, eller ring direkte til Jonas på 42 79 63 01.'
        )
      }
    } catch (err) {
      setStatus('error')
      setErrorMessage(
        'Kunne ikke få forbindelse til serveren. Tjek din internetforbindelse eller ring direkte til Jonas på 42 79 63 01.'
      )
    }
  }

  function handleChange(e) {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const isUnderline = variant === 'underline'

  return (
    <div className={isUnderline ? "w-full" : "bg-stone-900 border border-stone-800 rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl"}>
      {!hideHeader && !isUnderline && (
        <div className="mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2.5">
            <i className="fa-solid fa-file-signature" aria-hidden="true"></i>
            <span>Uforpligtende tilbud</span>
          </span>
          <h3 className="text-2xl sm:text-3xl font-bold text-stone-100 tracking-tight">
            Få pris på din opgave
          </h3>
          <p className="mt-2 text-sm sm:text-base text-stone-300">
            Beskriv kort dit projekt – f.eks. trampolinens mål, ønsket belægningsareal eller hvad der skal graves ud. Jonas vender hurtigt tilbage med en personlig vurdering.
          </p>
        </div>
      )}

      {status === 'success' ? (
        <div className="bg-emerald-950/80 border border-emerald-600/60 rounded-xl p-6 text-center text-emerald-100 animate-fadeIn">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-3 text-2xl">
            <i className="fa-solid fa-check" aria-hidden="true"></i>
          </div>
          <h4 className="text-xl font-bold text-white mb-2">
            Tak for din henvendelse!
          </h4>
          <p className="text-sm text-emerald-200/90 leading-relaxed max-w-md mx-auto">
            Vi har modtaget din besked. Jonas kigger på opgaven og kontakter dig hurtigst muligt på telefon eller e-mail med en konkret vurdering.
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
            <span>Send en ny besked</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className={isUnderline ? "space-y-6" : "space-y-4 sm:space-y-5"}>
          {/* Honeypot field (Strictly invisible to humans, no type=hidden) */}
          <div
            className="sr-only absolute -left-[9999px] -top-[9999px] opacity-0 pointer-events-none"
            aria-hidden="true"
          >
            <label htmlFor="website_url">Hjemmeside url (lad feltet stå tomt)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Top row: 50/50 split for Name and Email (or Phone) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label
                htmlFor="navn"
                className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1.5"
              >
                Fulde navn <span className="text-emerald-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="navn"
                  name="navn"
                  value={formData.navn}
                  onChange={handleChange}
                  placeholder="F.eks. Anders Jensen"
                  className={
                    isUnderline
                      ? `w-full py-2.5 bg-transparent border-0 border-b text-stone-100 text-base placeholder:text-stone-500 focus:outline-none transition-colors ${
                          errors.navn
                            ? 'border-red-500 focus:border-red-400'
                            : 'border-stone-600 focus:border-emerald-400'
                        }`
                      : `w-full px-4 py-3 rounded-lg bg-stone-950 border text-stone-100 text-sm placeholder:text-stone-500 focus:outline-none focus:ring-2 transition-colors ${
                          errors.navn
                            ? 'border-red-500 focus:ring-red-500/40'
                            : 'border-stone-700 focus:border-[#015205] focus:ring-[#015205]/30'
                        }`
                  }
                  aria-invalid={Boolean(errors.navn)}
                  aria-describedby={errors.navn ? 'navn-error' : undefined}
                />
              </div>
              {errors.navn && (
                <p id="navn-error" className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[11px]" aria-hidden="true"></i>
                  <span>{errors.navn}</span>
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1.5"
              >
                E-mailadresse <span className="text-emerald-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="navn@eksempel.dk"
                  className={
                    isUnderline
                      ? `w-full py-2.5 bg-transparent border-0 border-b text-stone-100 text-base placeholder:text-stone-500 focus:outline-none transition-colors ${
                          errors.email
                            ? 'border-red-500 focus:border-red-400'
                            : 'border-stone-600 focus:border-emerald-400'
                        }`
                      : `w-full px-4 py-3 rounded-lg bg-stone-950 border text-stone-100 text-sm placeholder:text-stone-500 focus:outline-none focus:ring-2 transition-colors ${
                          errors.email
                            ? 'border-red-500 focus:ring-red-500/40'
                            : 'border-stone-700 focus:border-[#015205] focus:ring-[#015205]/30'
                        }`
                  }
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
              </div>
              {errors.email && (
                <p id="email-error" className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[11px]" aria-hidden="true"></i>
                  <span>{errors.email}</span>
                </p>
              )}
            </div>
          </div>

          {/* Row 2: Phone and Service (or Full width phone) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Phone */}
            <div>
              <label
                htmlFor="telefonnummer"
                className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1.5"
              >
                Telefonnummer <span className="text-emerald-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="tel"
                  id="telefonnummer"
                  name="telefonnummer"
                  value={formData.telefonnummer}
                  onChange={handleChange}
                  placeholder="F.eks. 40 12 34 56"
                  className={
                    isUnderline
                      ? `w-full py-2.5 bg-transparent border-0 border-b text-stone-100 text-base placeholder:text-stone-500 focus:outline-none transition-colors ${
                          errors.telefonnummer
                            ? 'border-red-500 focus:border-red-400'
                            : 'border-stone-600 focus:border-emerald-400'
                        }`
                      : `w-full px-4 py-3 rounded-lg bg-stone-950 border text-stone-100 text-sm placeholder:text-stone-500 focus:outline-none focus:ring-2 transition-colors ${
                          errors.telefonnummer
                            ? 'border-red-500 focus:ring-red-500/40'
                            : 'border-stone-700 focus:border-[#015205] focus:ring-[#015205]/30'
                        }`
                  }
                  aria-invalid={Boolean(errors.telefonnummer)}
                  aria-describedby={errors.telefonnummer ? 'telefonnummer-error' : undefined}
                />
              </div>
              {errors.telefonnummer && (
                <p id="telefonnummer-error" className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[11px]" aria-hidden="true"></i>
                  <span>{errors.telefonnummer}</span>
                </p>
              )}
            </div>

            {/* Service selector */}
            <div>
              <label
                htmlFor="service"
                className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1.5"
              >
                Ydelse / Kategori
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                className={
                  isUnderline
                    ? "w-full py-2.5 bg-transparent border-0 border-b border-stone-600 text-stone-100 text-base focus:outline-none focus:border-emerald-400 transition-colors"
                    : "w-full px-4 py-3 rounded-lg bg-stone-950 border border-stone-700 text-stone-100 text-sm focus:outline-none focus:border-[#015205] focus:ring-2 focus:ring-[#015205]/30 transition-colors"
                }
              >
                <option value="Belægning" className="bg-stone-900 text-white">Belægning (terrasse, indkørsel, havegang)</option>
                <option value="Fundament & sokkel" className="bg-stone-900 text-white">Fundament & sokkel</option>
                <option value="Gravearbejde" className="bg-stone-900 text-white">Gravearbejde med minigraver</option>
                <option value="Nedgravning af trampolin" className="bg-stone-900 text-white">Nedgravning af trampolin</option>
                <option value="Anden opgave" className="bg-stone-900 text-white">Andet bygge- & anlægsarbejde</option>
              </select>
            </div>
          </div>

          {/* Task description: Full width */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="beskrivelse"
                className="block text-xs font-semibold uppercase tracking-wider text-stone-300"
              >
                Beskrivelse af opgaven <span className="text-emerald-400">*</span>
              </label>
              <span className="text-xs text-stone-400">
                F.eks. mål, placering eller haveadgang
              </span>
            </div>
            <textarea
              id="beskrivelse"
              name="beskrivelse"
              rows={compact ? 3 : 4}
              value={formData.beskrivelse}
              onChange={handleChange}
              placeholder="Fortæl kort om opgaven: Hvad skal udføres, hvor mange m2 eller trampolinens mål, og eventuelt hvordan adgangsforholdene er for maskiner på din grund..."
              className={
                isUnderline
                  ? `w-full py-2.5 bg-transparent border-0 border-b text-stone-100 text-base placeholder:text-stone-500 focus:outline-none transition-colors resize-y ${
                      errors.beskrivelse
                        ? 'border-red-500 focus:border-red-400'
                        : 'border-stone-600 focus:border-emerald-400'
                    }`
                  : `w-full px-4 py-3 rounded-lg bg-stone-950 border text-stone-100 text-sm placeholder:text-stone-500 focus:outline-none focus:ring-2 transition-colors resize-y ${
                      errors.beskrivelse
                        ? 'border-red-500 focus:ring-red-500/40'
                        : 'border-stone-700 focus:border-[#015205] focus:ring-[#015205]/30'
                    }`
              }
              aria-invalid={Boolean(errors.beskrivelse)}
              aria-describedby={errors.beskrivelse ? 'beskrivelse-error' : undefined}
            ></textarea>
            {errors.beskrivelse && (
              <p id="beskrivelse-error" className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[11px]" aria-hidden="true"></i>
                <span>{errors.beskrivelse}</span>
              </p>
            )}
          </div>

          {/* Submission error message if server failed */}
          {status === 'error' && (
            <div className="p-3.5 rounded-lg bg-red-950/80 border border-red-800 text-red-200 text-xs flex items-start gap-2.5">
              <i className="fa-solid fa-circle-exclamation text-red-400 mt-0.5" aria-hidden="true"></i>
              <div>
                <p className="font-semibold">Der opstod en fejl</p>
                <p className="mt-0.5">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Submit button: Pill-shaped full width when underline */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className={
                isUnderline
                  ? "w-full rounded-full py-4 bg-[#015205] hover:bg-[#026b07] active:bg-[#014204] text-white font-bold text-base tracking-wide shadow-lg shadow-[#015205]/30 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  : "w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3.5 rounded-lg bg-[#015205] hover:bg-[#026b07] active:bg-[#014204] text-white font-bold text-sm sm:text-base tracking-wide shadow-lg shadow-[#015205]/30 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              }
            >
              {status === 'submitting' ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                  <span>Sender opgave...</span>
                </>
              ) : (
                <>
                  <span>Få et uforpligtende tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </>
              )}
            </button>
          </div>

          <div className="pt-3 border-t border-stone-800 text-xs text-stone-400 flex flex-wrap items-center justify-between gap-2">
            <span>Haster det? Ring direkte til Jonas på:</span>
            <a
              href={COMPANY.phoneHref}
              className="text-stone-200 hover:text-emerald-400 font-bold whitespace-nowrap inline-flex items-center gap-1.5"
            >
              <i className="fa-solid fa-phone text-emerald-400 text-[11px]" aria-hidden="true"></i>
              <span>{COMPANY.phone}</span>
            </a>
          </div>
        </form>
      )}
    </div>
  )
}
